import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  IconButton,
} from '@mui/material';
import { ArrowForward, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { brandColors } from '../../theme';
import 'flag-icons/css/flag-icons.min.css';

interface MarketData {
  continent: string;
  count: string;
}

interface SampleCountry {
  country: string;
  continent: string;
  code: string;
}

const createSlug = (name: string): string => {
  return name.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const getContinentImagePath = (continentName: string): string => {
  const imageName = createSlug(continentName);
  return `/images/continents/${imageName}.png`;
};

const continentData: MarketData[] = [
  { continent: 'Europe', count: '150+' },
  { continent: 'North America', count: '80+' },
  { continent: 'South America', count: '40+' },
  { continent: 'Asia', count: '100+' },
  { continent: 'Africa', count: '40+' },
  { continent: 'Oceania', count: '25+' },
];

const sampleCountries: SampleCountry[] = [
  { country: 'United States', continent: 'North America', code: 'us' },
  { country: 'Australia', continent: 'Oceania', code: 'au' },
  { country: 'Japan', continent: 'Asia', code: 'jp' },
  { country: 'Israel', continent: 'Asia', code: 'il' },
  { country: 'UK', continent: 'Europe', code: 'gb' },
  { country: 'Germany', continent: 'Europe', code: 'de' },
  { country: 'France', continent: 'Europe', code: 'fr' },
  { country: 'Spain', continent: 'Europe', code: 'es' },
  { country: 'South Africa', continent: 'Africa', code: 'za' },
  { country: 'Argentina', continent: 'South America', code: 'ar' },
  { country: 'Brazil', continent: 'South America', code: 'br' },
];

export const MarketExplorer: React.FC = () => {
  const navigate = useNavigate();
  const [carouselIndex, setCarouselIndex] = useState(0);
  const countriesPerPage = 6;
  const maxIndex = Math.ceil(sampleCountries.length / countriesPerPage) - 1;

  const handlePrevious = () => {
    setCarouselIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
  };

  const handleNext = () => {
    setCarouselIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
  };

  const handleContinentClick = (continent: string) => {
    const continentSlug = createSlug(continent);
    navigate(`/global-market-reports/${continentSlug}`);
  };

  const handleCountryClick = (country: SampleCountry) => {
    const continentSlug = createSlug(country.continent);
    // Special handling for UK to maintain correct routing
    const countrySlug = country.country === 'UK' ? 'united-kingdom' : createSlug(country.country);
    navigate(`/global-market-reports/${continentSlug}/${countrySlug}`);
  };

  const visibleCountries = sampleCountries.slice(
    carouselIndex * countriesPerPage,
    (carouselIndex + 1) * countriesPerPage
  );

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: brandColors.white,
      }}
    >
      <Container maxWidth="lg">
        <Paper
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 3,
            border: `2px solid ${brandColors.indigo}`,
            backgroundColor: brandColors.white,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                color: brandColors.indigo,
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              }}
            >
              Discover Your Markets of Interest
            </Typography>
            
            <Typography
              variant="h6"
              sx={{
                color: brandColors.mediumGray,
                fontWeight: 400,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              Browse our extensive coverage across 6 continents
            </Typography>
          </Box>

          {/* Continent Grid */}
          <Grid container spacing={2} sx={{ mb: 6 }}>
            {continentData.map((data, index) => (
              <Grid item xs={6} sm={4} key={index}>
                <Card
                  onClick={() => handleContinentClick(data.continent)}
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: `1px solid ${brandColors.lightGray}`,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      backgroundColor: `${brandColors.gold}10`,
                      borderColor: brandColors.gold,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={getContinentImagePath(data.continent)}
                      alt={data.continent}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        maxWidth: 120,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: brandColors.indigo,
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    {data.continent}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: brandColors.mediumGray,
                    }}
                  >
                    {data.count} markets
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Sample Countries Carousel */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: brandColors.indigo,
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
              }}
            >
              Popular Countries
            </Typography>
            
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                }}
              >
                <IconButton
                  onClick={handlePrevious}
                  sx={{
                    backgroundColor: brandColors.lightGray,
                    '&:hover': {
                      backgroundColor: brandColors.gold,
                      color: brandColors.white,
                    },
                  }}
                >
                  <ArrowBack />
                </IconButton>

                <Grid container spacing={2} sx={{ maxWidth: 1000 }}>
                  {visibleCountries.map((country, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                      <Card
                        onClick={() => handleCountryClick(country)}
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: `${brandColors.blue}10`,
                            transform: 'scale(1.05)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        <Box
                          className={`fi fi-${country.code}`}
                          sx={{
                            fontSize: 48,
                            mb: 1,
                            display: 'inline-block',
                            lineHeight: 1,
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: brandColors.indigo,
                            mb: 1,
                          }}
                        >
                          {country.country}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: brandColors.blue,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            opacity: 0.8,
                            fontSize: '0.75rem',
                          }}
                        >
                          View Markets <ArrowForward fontSize="small" />
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <IconButton
                  onClick={handleNext}
                  sx={{
                    backgroundColor: brandColors.lightGray,
                    '&:hover': {
                      backgroundColor: brandColors.gold,
                      color: brandColors.white,
                    },
                  }}
                >
                  <ArrowForward />
                </IconButton>
              </Box>

              {/* Carousel Indicators */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1,
                  mt: 3,
                }}
              >
                {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor:
                        index === carouselIndex
                          ? brandColors.gold
                          : brandColors.lightGray,
                      cursor: 'pointer',
                      transition: 'background-color 0.3s ease',
                    }}
                    onClick={() => setCarouselIndex(index)}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};